import {
  EnableDisableLockerActionHandlers,
  EnableDisableLockerActionProps,
  EnableDisableLockerActionState,
  useEnableDisableLockerAction
} from './enable-disable-locker-action.hook';
import { DialogActions, DialogContent, Grid, TextField } from '@mui/material';
import { Modal } from '../../../../../ui/components/pure/modal/modal.component';
import { TextButton } from '../../../../../ui/components/pure/text-button/text-button.component';
import { Alert, AlertTitle } from '@mui/material';
import { Snackbar } from '../../../../../ui/components/pure/snackbar/snackbar.component';
import { parseErrorMessage } from '../../../../../core/api/api.context';
import { UseFormRegister, UseFormSetValue, useForm } from 'react-hook-form';
import { Dropdown } from '../../../../../ui/components/pure/dropdown/dropdown.component';
import { notePrivacyOptions } from './enable-disable-locker-action.constants';
import { DeliveryContainer } from '../../../models/delivery-container.model';

export function EnableDisableLockerActionModal({
  lockerID,
  containerType,
  onAbort,
  ...rest
}: EnableDisableLockerActionProps) {
  const { state, handlers, locker, hub, lockerContents } = useEnableDisableLockerAction({
    lockerID,
    containerType,
    onAbort,
    ...rest
  });
  if (locker.error || hub.error || lockerContents.error) {
    return (
      <Snackbar severity="error" onClose={() => onAbort()}>
        Sorry there was an error getting the details of the locker.
      </Snackbar>
    );
  }
  return (
    <EnableDisableLockerModalComponent
      state={state}
      handlers={handlers}
      loading={locker.isLoading || hub.isLoading || lockerContents.isLoading}
      locker={locker.data}
    />
  );
}

interface FormData {
  note: string;
  visibility: string;
}
interface DisplayNoteFieldsProps {
  register: UseFormRegister<FormData>;
  setValue: UseFormSetValue<FormData>;
}

export function EnableDisableLockerModalComponent({
  locker,
  handlers,
  state,
  loading
}: {
  locker?: DeliveryContainer;
  hubName?: string;
  state: EnableDisableLockerActionState;
  handlers: EnableDisableLockerActionHandlers;
  loading: boolean;
}) {
  const { register, handleSubmit, setValue } = useForm<FormData>();
  const onSubmit = (data: FormData) => {
    const { note, visibility } = data;
    handlers.onSendRequest(note, visibility === 'private');
  };
  const actionBtnColor = locker?.status === 'active' ? 'danger' : 'primary';
  const actionBtnLabel = state.error ? 'Retry' : locker?.status === 'active' ? 'Disable' : 'Enable';

  return (
    <Modal
      title={`${locker?.status === 'active' ? 'Disable' : 'Enable'} locker?`}
      loading={loading || state.loading}
      open={true}
      maxWidth="xs"
      onClose={handlers.onCancelIntent}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent style={{ padding: '16px 24px', fontSize: '14px' }}>
          {state.error ? (
            <p>
              <Alert variant="outlined" severity="error">
                <AlertTitle>Something went wrong</AlertTitle>
                {parseErrorMessage(state.error.message)}
              </Alert>
              <p>{'It looks like locker is not updated. Please retry.'}</p>
            </p>
          ) : null}
          {locker ? (
            <>
              {locker?.status === 'active' ? (
                <p>
                  Any delivery currently in the locker will still be able to be collected, but no new deliveries can be
                  made while disabled.
                </p>
              ) : null}
              <p>Why you are {locker.status === 'active' ? 'disabling' : 'enabling'} the locker?</p>
              {NoteFields({ register, setValue })}
            </>
          ) : null}
        </DialogContent>
        <DialogActions style={{ borderTop: '1px solid #EBEDF2', padding: '16px 24px' }}>
          <TextButton color="secondary" variant="outlined" onClick={handlers.onCancelIntent}>
            Cancel
          </TextButton>
          <TextButton color={actionBtnColor} variant="outlined" type="submit">
            {actionBtnLabel}
          </TextButton>
        </DialogActions>
      </form>
    </Modal>
  );
}

function NoteFields({ register, setValue }: DisplayNoteFieldsProps) {
  return (
    <>
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 6
          }}
        >
          <TextField
            variant="standard"
            label="Add note"
            fullWidth
            type="text"
            {...register('note')}
            required
            slotProps={{
              input: {
                style: {
                  height: '29.63px'
                }
              },

              inputLabel: {
                style: {
                  color: '#0000008a'
                }
              }
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 4,
            md: 4
          }}
        >
          <Dropdown
            fullWidth
            required
            dropdownVariant="floating"
            slotProps={{
              inputLabel: {
                style: {
                  marginTop: 0
                }
              }
            }}
            menuItems={notePrivacyOptions}
            placeholder="Select privacy"
            onChange={(e) => {
              setValue('visibility', e.target.value);
            }}
          />
        </Grid>
      </Grid>
    </>
  );
}
